import { useSyncExternalStore } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFileCsv, faFileExcel, faFileLines } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import {
  downloadExportedChatFile,
  getExportedChatFile,
  subscribeToExportedChatFileStore,
} from './exportedChatFileStore';

const EXPORT_FILE_FORMATS = ['xlsx', 'csv', 'tsv', 'md', 'txt'] as const;

// part.input（unknown）から表示用のダウンロードファイル名を復元する。履歴復元後など Blob が
// 失効しても、ツール引数はメッセージに残るためファイル名は表示できる。
function deriveFilename(input: unknown): string | undefined {
  if (typeof input !== 'object' || input === null) return undefined;
  const record = input as Record<string, unknown>;
  const filename = typeof record.filename === 'string' ? record.filename : undefined;
  const format = EXPORT_FILE_FORMATS.find((candidate) => candidate === record.format);
  if (!filename || !format) return undefined;
  const base = filename.replace(/\.(xlsx|csv|tsv|md|txt)$/iu, '');
  return `${base}.${format}`;
}

function iconForFilename(filename: string) {
  if (filename.endsWith('.md') || filename.endsWith('.txt')) return faFileLines;
  return filename.endsWith('.csv') || filename.endsWith('.tsv') ? faFileCsv : faFileExcel;
}

type Props = {
  toolCallId: string;
  input: unknown;
};

/**
 * exportFile ツールで生成したファイルのダウンロードカード。生成しても自動ではダウンロードせず、
 * このカードのボタンをクリックしたときだけダウンロードする。
 * Blob はセッション中だけストアに保持されるため、履歴復元後はダウンロード不可となり再生成を促す。
 */
export function ExportFileCard({ toolCallId, input }: Props) {
  const { t } = useTranslation();
  // ストアを購読し、他のカードのダウンロードで自分の分が evict されたときも自動で失効表示に
  // 切り替わるようにする（購読しないと、evict後もボタン表示のまま残ってしまう）。
  const stored = useSyncExternalStore(subscribeToExportedChatFileStore, () => getExportedChatFile(toolCallId));
  const filename = stored?.filename ?? deriveFilename(input) ?? t('viewModel.aiChat.attachmentFallbackName');

  return (
    <div className="my-1 flex flex-col gap-1.5 rounded border border-gray-300 bg-white p-2 text-xs">
      <div className="flex items-center gap-1.5">
        <FontAwesomeIcon icon={iconForFilename(filename)} className="shrink-0 text-emerald-600" />
        <span className="truncate font-medium text-gray-700" title={filename}>
          {filename}
        </span>
      </div>
      {stored ? (
        <button
          type="button"
          onClick={() => {
            // クリックハンドラのクロージャで Blob（stored）を直接保持すると、Map から evict
            // されてもこのコンポーネントが再レンダーされない限り参照が残りメモリ上限が効かなくなる。
            // クリック時にストアから読み直すことで、保持するのは toolCallId（文字列）だけにする。
            const latest = getExportedChatFile(toolCallId);
            if (latest) downloadExportedChatFile(toolCallId, latest);
          }}
          className="flex items-center gap-1.5 self-start rounded border border-gray-300 px-2 py-1 text-gray-600 hover:bg-gray-50 hover:text-blue-600"
        >
          <FontAwesomeIcon icon={faDownload} />
          {t('viewModel.aiChat.exportCardDownloadLabel')}
        </button>
      ) : (
        <span className="text-gray-400">{t('viewModel.aiChat.exportCardExpiredMessage')}</span>
      )}
      <span className="text-gray-400">{t('viewModel.aiChat.exportCardNote')}</span>
    </div>
  );
}
